<template>
  <div class="dashboard-view">
    <h1>Dashboard</h1>
    <div v-if="isLoading" class="loading-message">
      <p>Loading statistics...</p>
    </div>
    <div v-if="errorMessage" class="error-message">
      <p>{{ errorMessage }}</p>
    </div>
    <div v-if="!isLoading && statistics" class="statistics-grid">
      <div class="stat-card">
        <h2>Reviews Today</h2>
        <p>{{ statistics.reviews_today }}</p>
      </div>
      <div class="stat-card">
        <h2>New Cards Today</h2>
        <p>{{ statistics.new_cards_reviewed_today }}</p>
      </div>
      <div class="stat-card">
        <h2>Reviews This Week</h2>
        <p>{{ statistics.reviews_this_week }}</p>
      </div>
      <div class="stat-card">
        <h2>Total Reviews</h2>
        <p>{{ statistics.total_reviews_all_time }}</p>
      </div>
      <div class="stat-card">
        <h2>Average Score (All Cards)</h2>
        <p class="score-display">{{ formatScoreAsPercentage(statistics.overall_average_score) }}</p>
        <p v-if="statistics.overall_average_score !== null" class="score-subtitle">
          {{ formatScore(statistics.overall_average_score) }} / 1.00
        </p>
      </div>
      <div class="stat-card">
        <h2>Total Cards</h2>
        <p>{{ statistics.total_cards }}</p>
        <p v-if="statistics.total_pairs" class="stat-subtitle">{{ statistics.total_pairs }} pairs</p>
      </div>
      <div class="stat-card">
        <h2>Cards Learned</h2>
        <p>{{ statistics.cards_learned }} ({{ statistics.percentage_learned }}%)</p>
      </div>
      <div class="stat-card">
        <h2>Cards Not Reviewed</h2>
        <p>{{ statistics.cards_not_reviewed || 0 }}</p>
      </div>
      <div class="stat-card">
        <h2>Cards Mastered</h2>
        <p>{{ statistics.cards_mastered }}</p>
      </div>
    </div>

    <!-- Daily Activity Chart -->
    <div v-if="!isLoading && statistics && statistics.daily_stats && statistics.daily_stats.length > 0" class="chart-section">
      <h2>Daily Activity (Last 30 Days)</h2>
      <div class="chart-container">
        <div class="chart-legend">
          <span class="legend-item"><span class="legend-color added"></span> Cards Added</span>
          <span class="legend-item"><span class="legend-color reviews"></span> Reviews</span>
        </div>
        <div class="line-chart-wrapper">
          <div
            v-if="chartHover"
            class="chart-tooltip"
            :style="chartHoverStyle"
          >
            <div class="tooltip-date">{{ formatChartDate(chartHover.date) }}</div>
            <div class="tooltip-row">
              <span class="tooltip-dot added"></span>
              <span>{{ chartHover.cards_added }} added</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-dot reviews"></span>
              <span>{{ chartHover.reviews }} reviewed</span>
            </div>
          </div>
          <svg
            ref="chartSvg"
            class="line-chart"
            :viewBox="`0 0 ${chartViewBoxWidth} ${chartViewBoxHeight}`"
            preserveAspectRatio="none"
          >
            <polyline class="line line-added" :points="chartPolylineAdded"></polyline>
            <polyline class="line line-reviews" :points="chartPolylineReviewed"></polyline>
            <g>
              <circle
                v-for="(point, idx) in chartPointsAdded"
                :key="`added-${idx}`"
                class="dot dot-added"
                :cx="point.x"
                :cy="point.y"
                r="2"
                @mouseenter="setChartHover(point.day, point.x, point.y)"
                @mouseleave="clearChartHover"
              ></circle>
            </g>
            <g>
              <circle
                v-for="(point, idx) in chartPointsReviewed"
                :key="`reviewed-${idx}`"
                class="dot dot-reviews"
                :cx="point.x"
                :cy="point.y"
                r="2"
                @mouseenter="setChartHover(point.day, point.x, point.y)"
                @mouseleave="clearChartHover"
              ></circle>
            </g>
          </svg>
          <div class="chart-dates">
            <span
              v-for="tick in chartDateTicks"
              :key="`date-${tick.idx}`"
              class="chart-date"
              :style="{ left: `${(tick.idx / Math.max(statistics.daily_stats.length - 1, 1)) * 100}%` }"
            >
              {{ formatChartDate(tick.day.date) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && !statistics && !errorMessage" class="no-data-message">
        <p>No statistics data available yet. Start reviewing some cards!</p>
    </div>

    <div v-if="!isLoading && filteredSessions.length > 0" class="sessions-section">
      <h2>Study Sessions</h2>
      <div class="sessions-list">
        <div v-for="session in filteredSessions" :key="session.session_id" class="session-card">
          <div class="session-header-row">
            <span class="session-date">{{ formatSessionDate(session.start_time) }}</span>
            <span v-if="session.is_active" class="session-badge active">Active</span>
          </div>
          <div class="session-stats-row">
            <div class="session-stat">
              <span class="stat-label">Time:</span>
              <span class="stat-value">{{ formatTime(session.active_minutes) }}</span>
            </div>
            <div class="session-stat">
              <span class="stat-label">Cards:</span>
              <span class="stat-value">{{ session.cards_reviewed }}</span>
            </div>
            <div class="session-stat" v-if="session.average_score !== null">
              <span class="stat-label">Avg Score:</span>
              <span class="stat-value">{{ (session.average_score * 100).toFixed(0) }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/ApiService';

export default {
  name: 'DashboardView',
  data() {
    return {
      statistics: null,
      studySessions: [],
      isLoading: true,
      isLoadingSessions: false,
      errorMessage: '',
      chartHover: null,
      chartHoverStyle: { left: '0%', top: '0%' },
      chartViewBoxWidth: 100,
      chartViewBoxHeight: 100,
      chartPadding: 6,
      chartResizeObserver: null,
      chartObserverAttached: false
    };
  },
  computed: {
    filteredSessions() {
      return (this.studySessions || []).filter((session) => (session.cards_reviewed || 0) > 0);
    },
    chartMax() {
      if (!this.statistics || !this.statistics.daily_stats) return 1;
      return Math.max(
        ...this.statistics.daily_stats.map((day) => Math.max(day.cards_added, day.reviews)),
        1
      );
    },
    chartPointsAdded() {
      if (!this.statistics || !this.statistics.daily_stats) return [];
      const count = this.statistics.daily_stats.length;
      const span = Math.max(count - 1, 1);
      const usableWidth = this.chartViewBoxWidth - this.chartPadding * 2;
      const usableHeight = this.chartViewBoxHeight - this.chartPadding * 2;
      return this.statistics.daily_stats.map((day, idx) => {
        const x = this.chartPadding + (idx / span) * usableWidth;
        const y = this.chartViewBoxHeight - this.chartPadding - (day.cards_added / this.chartMax) * usableHeight;
        return { x, y, day };
      });
    },
    chartPointsReviewed() {
      if (!this.statistics || !this.statistics.daily_stats) return [];
      const count = this.statistics.daily_stats.length;
      const span = Math.max(count - 1, 1);
      const usableWidth = this.chartViewBoxWidth - this.chartPadding * 2;
      const usableHeight = this.chartViewBoxHeight - this.chartPadding * 2;
      return this.statistics.daily_stats.map((day, idx) => {
        const x = this.chartPadding + (idx / span) * usableWidth;
        const y = this.chartViewBoxHeight - this.chartPadding - (day.reviews / this.chartMax) * usableHeight;
        return { x, y, day };
      });
    },
    chartPolylineAdded() {
      return this.chartPointsAdded.map((point) => `${point.x},${point.y}`).join(' ');
    },
    chartPolylineReviewed() {
      return this.chartPointsReviewed.map((point) => `${point.x},${point.y}`).join(' ');
    },
    chartDateTicks() {
      if (!this.statistics || !this.statistics.daily_stats) return [];
      const total = this.statistics.daily_stats.length;
      if (total <= 1) return this.statistics.daily_stats.map((day, idx) => ({ day, idx }));
      const step = Math.ceil(total / 6);
      return this.statistics.daily_stats
        .map((day, idx) => ({ day, idx }))
        .filter(({ idx }) => idx === 0 || idx === total - 1 || idx % step === 0);
    }
  },
  watch: {
    statistics() {
      this.$nextTick(() => {
        this.updateChartViewBox();
        if (this.chartResizeObserver && this.$refs.chartSvg && !this.chartObserverAttached) {
          this.chartResizeObserver.observe(this.$refs.chartSvg);
          this.chartObserverAttached = true;
        }
      });
    }
  },
  methods: {
    async fetchStatistics() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        // Use Card statistics endpoint instead of Sentence endpoint
        const response = await ApiService.getCardStatistics();
        if (response.status === 200) {
          if (response.data) {
            this.statistics = response.data;
          } else {
            this.statistics = null;
          }
        } else {
          this.errorMessage = "Could not load statistics. The server didn't return valid data.";
          this.statistics = null;
        }
      } catch (error) {
        console.error("Error fetching statistics:", error);
        this.errorMessage = 'Failed to load statistics. Please check your connection or try again later.';
        this.statistics = null;
      }
      this.isLoading = false;
    },
    formatScore(score) {
        if (score === null || score === undefined) return 'N/A';
        return parseFloat(score).toFixed(2);
    },
    formatScoreAsPercentage(score) {
        if (score === null || score === undefined) return 'N/A';
        const percentage = parseFloat(score) * 100;
        return `${percentage.toFixed(1)}%`;
    },
    async fetchStudySessions() {
      this.isLoadingSessions = true;
      try {
        const response = await ApiService.getStudySessions();
        if (response && response.status === 200 && response.data) {
          this.studySessions = response.data.sessions || [];
        }
      } catch (error) {
        console.error("Error fetching study sessions:", error);
        // Don't show error for sessions, just log it
      } finally {
        this.isLoadingSessions = false;
      }
    },
    formatSessionDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return date.toLocaleDateString(undefined, options);
    },
    formatTime(minutes) {
      if (!minutes && minutes !== 0) return '0:00';
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      const secs = Math.floor((minutes % 1) * 60);
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    formatChartDate(dateStr) {
      const date = new Date(dateStr);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    updateChartViewBox() {
      const svg = this.$refs.chartSvg;
      if (!svg) return;
      const width = svg.clientWidth || 0;
      const height = svg.clientHeight || 0;
      if (!width || !height) return;
      this.chartViewBoxWidth = width;
      this.chartViewBoxHeight = height;
    },
    setChartHover(day, x, y) {
      const width = this.chartViewBoxWidth || 1;
      const xPercent = (x / width) * 100;
      const yPercent = (y / this.chartViewBoxHeight) * 100;
      this.chartHover = day;
      this.chartHoverStyle = {
        left: `${xPercent}%`,
        top: `${Math.max(yPercent, 5)}%`
      };
    },
    clearChartHover() {
      this.chartHover = null;
    }
  },
  mounted() {
    this.fetchStatistics();
    this.fetchStudySessions();
    this.$nextTick(() => {
      this.updateChartViewBox();
      if (window.ResizeObserver) {
        this.chartResizeObserver = new ResizeObserver(() => {
          this.updateChartViewBox();
        });
        if (this.$refs.chartSvg) {
          this.chartResizeObserver.observe(this.$refs.chartSvg);
          this.chartObserverAttached = true;
        }
      } else {
        window.addEventListener('resize', this.updateChartViewBox);
      }
    });
  },
  beforeUnmount() {
    if (this.chartResizeObserver) {
      this.chartResizeObserver.disconnect();
      this.chartResizeObserver = null;
      this.chartObserverAttached = false;
    } else {
      window.removeEventListener('resize', this.updateChartViewBox);
    }
  },
};
</script>

<style scoped>
.dashboard-view {
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.loading-message, .error-message, .no-data-message {
  text-align: center;
  padding: 20px;
  background-color: #f0f0f0;
  border-radius: 8px;
  margin: 20px auto;
  max-width: 500px;
}

.error-message {
  color: red;
  background-color: #ffe0e0;
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.stat-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.stat-card h2 {
  font-size: 1.1em;
  color: #333;
  margin-top: 0;
  margin-bottom: 10px;
}

.stat-card p {
  font-size: 1.8em;
  color: #007bff;
  margin: 0;
  font-weight: bold;
}

.score-display {
  font-size: 2.2em !important;
  color: #28a745 !important;
  margin-bottom: 5px !important;
}

.score-subtitle {
  font-size: 0.9em !important;
  color: #666 !important;
  font-weight: normal !important;
  margin-top: 5px !important;
}

.sessions-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 2px solid #e0e0e0;
}

.sessions-section h2 {
  margin-bottom: 20px;
  color: #333;
  font-size: 1.5em;
}

.sessions-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.session-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.session-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.session-date {
  font-size: 0.95em;
  color: #555;
  font-weight: 500;
}

.session-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
}

.session-badge.active {
  background-color: #28a745;
  color: white;
}

.session-stats-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.session-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.session-stat .stat-label {
  font-size: 0.75em;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.session-stat .stat-value {
  font-size: 1.2em;
  font-weight: bold;
  color: #007bff;
}

.stat-subtitle {
  font-size: 0.85em !important;
  color: #666 !important;
  font-weight: normal !important;
  margin-top: 5px !important;
}

.chart-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #e0e0e0;
}

.chart-section h2 {
  margin-bottom: 20px;
  color: #333;
  font-size: 1.5em;
}

.chart-container {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px 14px 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.chart-legend {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9em;
  color: #666;
}

.legend-color {
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.legend-color.added {
  background-color: #28a745;
}

.legend-color.reviews {
  background-color: #007bff;
}

.line-chart-wrapper {
  position: relative;
  width: 100%;
}

.line-chart {
  display: block;
  width: 100%;
  height: 140px;
  overflow: visible;
}

.line {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.line-added {
  stroke: #28a745;
}

.line-reviews {
  stroke: #007bff;
}

.dot {
  cursor: pointer;
  transition: r 0.1s ease;
}

.dot-added {
  fill: #28a745;
}

.dot-reviews {
  fill: #007bff;
}

.dot:hover {
  r: 3;
}

.chart-tooltip {
  position: absolute;
  transform: translate(-50%, -110%);
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.85em;
  color: #333;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  pointer-events: none;
  z-index: 2;
  min-width: 120px;
}

.tooltip-date {
  font-weight: 600;
  margin-bottom: 6px;
}

.tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.tooltip-row:last-child {
  margin-bottom: 0;
}

.tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.tooltip-dot.added {
  background-color: #28a745;
}

.tooltip-dot.reviews {
  background-color: #007bff;
}

.chart-dates {
  position: relative;
  width: 100%;
  height: 14px;
  margin-top: 6px;
}

.chart-date {
  font-size: 0.65em;
  color: #999;
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
}
</style>
